import React from "react";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { get, isEmpty, size } from "lodash";
import { getPayrollHistory } from "actions/payrollActions";
import { wrapError } from "utils/errorHelper";
import { FiDownload, FiList } from "react-icons/fi";
import { simulateLinkClickNewTab } from "utils/dom";
import { getEmployerDownloadUrl } from "services/documentService";
import { formatCurrency } from "utils/number";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { payrollConstants } from "actions/types";
import {
  payrollStatusToEnglish,
  payrollStatusToType,
} from "statics/statusToEnglishMapping";

import moment from "moment";
import StatusBox from "components/StatusBox";
import IconTable, {
  type IconTableColumn,
} from "../../../../components/IconTable";
import IconMessageCard from "components/IconMessageCard";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import type { RootState } from "types/state";

import "./ProcessGroupsTable.scss";

const PENDING_DOCUMENT_ID = "-1";

const actions = [payrollConstants.GET_PAYROLL_HISTORY];

const errorSelector = createErrorSelector(actions);
const loadingSelector = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    loading: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getPayrollHistory,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  groupId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface PayrollPeriod {
  displayStart: string;
  displayEnd: string;
}

interface PayrollRow {
  groupName: string;
  period: PayrollPeriod;
  totalAmount: number;
  status: string;
  payrollDocumentId: string;
}

interface State {
  /** set in initial state but never read or written afterward */
  isLoading?: boolean;
  error: string;
  payrolls: PayrollRow[];
}

class ProcessContributions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      error: "",
      payrolls: [],
    };
  }

  componentDidMount() {
    void this.props
      .getPayrollHistory(this.props.client as ApolloClient<object>)
      .then((response) => {
        const payrolls = get(
          response,
          "data.payrollHistory",
          []
        ) as PayrollRow[];
        return this.setState({
          payrolls,
        });
      });
  }

  _renderError = () => {
    if (!isEmpty(this.props.error) || !isEmpty(this.state.error)) {
      const message = isEmpty(this.props.error)
        ? this.state.error
        : this.props.error;
      return <Alert type="error" msg={message} />;
    }
  };

  _renderContent = () => {
    const columns = this._buildColumns();
    const numberOfGroups = size(this.state.payrolls);
    const payrollEmpty = isEmpty(this.state.payrolls);

    if (this.props.loading) {
      return <IconSpinner centered />;
    }

    return (
      <Card className="process-contributions">
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <div className="widget-header">
          <Card.Title>
            Payroll History:
            <p className="invite-number">
              Payrolls (<p className="number">{numberOfGroups}</p>)
            </p>
          </Card.Title>
        </div>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        {!payrollEmpty && (
          <IconTable columns={columns} data={this.state.payrolls} />
        )}
        {payrollEmpty && (
          <div style={{ borderTop: "1px solid #f2f4f7" }}>
            <IconMessageCard
              header={"No processed payrolls"}
              subheader="Please process a payroll to view payroll history"
              icon={<FiList color="white" stroke="#60A4BF" size={16} />}
            />
          </div>
        )}
      </Card>
    );
  };

  _buildColumns = (): Array<IconTableColumn<PayrollRow>> => {
    const commonDateFormat = "MMM Do YYYY";

    const columns: Array<IconTableColumn<PayrollRow>> = [
      {
        label: "Group Name",
        key: "groupName",
      },

      {
        label: "Payroll Period",
        key: "error",
        customComponent: (props) => {
          const start = props.period.displayStart;
          const end = props.period.displayEnd;
          const period = `${moment(start).format(commonDateFormat)} - ${moment(
            end
          ).format(commonDateFormat)}`;
          return period;
        },
      },
      {
        label: "Amount",
        customComponent: (props) => <> {formatCurrency(props.totalAmount)}</>,
      },
      {
        label: "Status",
        customComponent: (props) => (
          <StatusBox
            status={
              payrollStatusToEnglish[
                props.status as keyof typeof payrollStatusToEnglish
              ]
            }
            type={
              payrollStatusToType[
                props.status as keyof typeof payrollStatusToType
              ]
            }
          />
        ),
      },
      {
        label: "Action",

        customComponent: (props) => (
          <>
            {props.payrollDocumentId === PENDING_DOCUMENT_ID ? (
              <StatusBox type="light" status="Processing" />
            ) : (
              <a
                className="link"
                target="_blank"
                onClick={() => this._downloadFile(props.payrollDocumentId)}
              >
                <FiDownload
                  fill={"#FFFFFF"}
                  color={"#FFFFFF"}
                  stroke={"#29b0c2"}
                  size={"20px"}
                />
              </a>
            )}
          </>
        ),
      },
    ];
    return columns;
  };

  _downloadFile(fileId: string) {
    getEmployerDownloadUrl(this.props.client as ApolloClient<object>, {
      documentId: fileId,
    })
      .then((response) => {
        // non-null assertion preserves original unconditional `.url` access
        simulateLinkClickNewTab(response!.url);
      })
      .catch((error) => {
        this.setState({ error: wrapError(error) });
      });
  }

  render() {
    return (
      <div>
        <div className="mega-container">
          <section className="page-title-wrap">
            <article className="text-cell">
              <h1 className="page-title">Payroll History</h1>
              {this._renderError()}
            </article>
          </section>
          <div className="main-content">{this._renderContent()}</div>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(ProcessContributions));
